import type { ElementType } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnimalPrintIcon,
  ChatCircleDotsIcon,
  House03Icon,
  MoreGridBigIcon,
} from '@/components/atoms/icons'
import { ROUTES } from '@/shared/constants/routes'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'
import type { FeatureFlag } from '@/shared/constants/feature-flags'
import { useFeatureFilter } from '@/shared/hooks/use-feature-enabled'
import { useActiveNavIndex } from '@/shared/hooks/use-active-nav-index'

export interface BottomNavItem {
  to: string
  label: string
  icon: ElementType<{ className?: string }>
  testId: string
  flag?: FeatureFlag
}

export function useBottomNav() {
  const { t } = useTranslation()

  const items = useFeatureFilter<BottomNavItem>([
    {
      to: ROUTES.HOME,
      label: t('nav.home'),
      icon: MoreGridBigIcon,
      testId: 'bottom-nav-home',
      flag: FEATURE_FLAGS.WEB_APP_DASHBOARD,
    },
    {
      to: ROUTES.PET,
      label: t('nav.pet'),
      icon: AnimalPrintIcon,
      testId: 'bottom-nav-pet',
      flag: FEATURE_FLAGS.WEB_APP_PET,
    },
    {
      to: ROUTES.RENTERS,
      label: t('nav.renters'),
      icon: House03Icon,
      testId: 'bottom-nav-renters',
      flag: FEATURE_FLAGS.WEB_APP_RENTERS,
    },
    {
      to: ROUTES.CLAIMS,
      label: t('nav.claims'),
      icon: ChatCircleDotsIcon,
      testId: 'bottom-nav-claims',
      flag: FEATURE_FLAGS.WEB_APP_CLAIMS,
    },
  ])

  const activeIndex = useActiveNavIndex(items)

  const indicatorStyle = {
    width: `calc((100% - 0.5rem) / ${items.length})`,
    transform: `translateX(${Math.max(activeIndex, 0) * 100}%)`,
  }

  const gridStyle = {
    gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
  }

  return {
    items,
    gridStyle,
    ariaLabel: t('nav.ariaLabel'),
    hasActive: activeIndex !== -1,
    indicatorStyle,
  }
}
